import React, { useCallback, useEffect, useState } from 'react';
import user_icon from '../../../assets/icons/user.svg';
import monthly_submissions from '../../../assets/icons/driverDetail/monthlysubmissions.svg';
import exceed_alcohol_limit from '../../../assets/icons/driverDetail/exceedalcohollimit.svg';
import monthly_working from '../../../assets/icons/driverDetail/monthlyworking.svg';
import axiosInstance from 'config/axiosInstance';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { EXCEED_FILTER_PARAM } from 'config/measurement';
import { durationText } from 'reusable/durationText';
import { businessDayStart, businessMonth, businessMonthRange, businessRangeQuery, businessZoneQuery } from 'config/time';
import { useTranslation } from 'react-i18next';

interface DriverAlcoholSubmissionsTableProps {
  id?: string;
  /**
   * A36（2026-11-03 和田様の UAT）。上部の月の切り替えで選んだ月（`YYYY-MM`）。月ごとの数字はこの月で数える。
   * 既定は今の月（業務時間帯）。
   */
  month?: string;
}
interface Profile {
  name?: string;
  phone?: string;
  email?: string;
  profile_picture?: string;
}
interface MonthlySubmissionTotal {
  count?: number;
}
interface MonthlySubmission {
  count?: number;
}
interface ExccedAlcoholLimitTotal {
  count?: number;
}
interface ExccedAlcoholLimit {
  count?: number;
}
interface MonthlyWorkingHoursTotal {
  totalHours: string;
}
interface MonthlyWorkingHours {
  monthlyHours: string;
}

/** `YYYY-MM-DD` を「11/1」の形にする（UAT A12。期間の表示）。 */
const shortDate = (date: string) => {
  const [, month, day] = date.split('-').map(Number);
  return `${month}/${day}`;
};

const DriverViewStatus: React.FC<DriverAlcoholSubmissionsTableProps> = ({
  id,
  month: selectedMonth,
}) => {
  const { t } = useTranslation('driver');
  const beginRequest = useLatestRequest();
  const month = selectedMonth ?? businessMonth();
  const requestKey = `${id}-${month}`;
  const [resultKey, setResultKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<unknown>(null);
  const [profile, setProfile] = useState<Profile | undefined>(undefined);
  const [monthlySubmissionsTotal, setMonthlySubmissionsTotal] = useState<
    MonthlySubmissionTotal | undefined
  >(undefined);
  const [monthlySubmissions, setMonthlySubmissions] = useState<
    MonthlySubmission | undefined
  >(undefined);
  const [exceedAlcoholLimitTotal, setExceedAlcoholLimitTotal] = useState<
    ExccedAlcoholLimitTotal | undefined
  >(undefined);
  const [exceedAlcoholLimit, setExceedAlcoholLimit] = useState<
    ExccedAlcoholLimit | undefined
  >(undefined);
  const [monthlyWorkingHoursTotal, setMonthlyWorkingHoursTotal] = useState<
    MonthlyWorkingHoursTotal | undefined
  >(undefined);
  const [monthlyWorkingHours, setMonthlyWorkingHours] = useState<
    MonthlyWorkingHours | undefined
  >(undefined);
  // UAT A32。その月の乗務日数（出勤した日の数。サーバーの total_hour の work_days）。
  const [workDays, setWorkDays] = useState<number | undefined>(undefined);
  // UAT A12。数えた期間（「11/1〜11/30」）。今の月なら「当月」と出す。
  const { start: periodStart, end: periodEnd } = businessMonthRange(new Date(businessDayStart(`${month}-01`)));
  const isCurrentMonth = month === businessMonth();
  const period = isCurrentMonth
    ? t('view.status.period-current')
    : t('view.status.period-month', { year: Number(month.slice(0, 4)), month: Number(month.slice(5, 7)) });
  const range = t('view.status.range', { start: shortDate(periodStart), end: shortDate(periodEnd) });

  // EV-493。勤務時間の合計はサーバーで集計する（以前は勤怠を全件取って画面で足していた）。
  // 退勤した行だけの (退勤 − 出勤) の合計で、以前の calculateTotalHours と同じ（未退勤は数えない）。
  const completedHours = (data: { total_hour?: number } | undefined): string =>
    Number(data?.total_hour ?? 0).toFixed(2);

  const fetchProfileData = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setFetchError(null);
    try {
      const { start, end } = businessMonthRange(new Date(businessDayStart(`${month}-01`)));
      const monthRange = businessRangeQuery('testing_time', start, end);
      // すべて同じ運転者・同じ月の結果が揃ってから反映する。
      const [profileResult, total, monthly, exceedTotal, exceedMonthly, totalAttendance, monthlyAttendance, monthlyDays] = await Promise.all([
        axiosInstance.get(`/v1/drivers/${id}/`),
        axiosInstance.get(`v1/alcohol_tests/?user=${id}`),
        axiosInstance.get(`v1/alcohol_tests/?${monthRange}user=${id}`),
        axiosInstance.get(`v1/alcohol_tests/?${EXCEED_FILTER_PARAM}&user=${id}`),
        axiosInstance.get(`v1/alcohol_tests/?${EXCEED_FILTER_PARAM}&${monthRange}user=${id}`),
        axiosInstance.get(`/v1/attendances/total_hour/?check_out__isnull=false&user=${id}`),
        axiosInstance.get(`/v1/attendances/total_hour/?check_out__isnull=false&${businessRangeQuery('check_in', start, end)}user=${id}`),
        // UAT A32。乗務日数は出勤した日の数（退勤していない今日の出勤も数えるので、退勤の絞り込みを付けない）。
        // 日の境は画面と同じ時間帯（time_zone）。
        axiosInstance.get(`/v1/attendances/total_hour/?${businessZoneQuery()}${businessRangeQuery('check_in', start, end)}user=${id}`),
      ]);
      if (!isCurrent()) return;
      setProfile(profileResult.data);
      setMonthlySubmissionsTotal(total.data);
      setMonthlySubmissions(monthly.data);
      setExceedAlcoholLimitTotal(exceedTotal.data);
      setExceedAlcoholLimit(exceedMonthly.data);
      setMonthlyWorkingHoursTotal({ totalHours: completedHours(totalAttendance.data) });
      setMonthlyWorkingHours({ monthlyHours: completedHours(monthlyAttendance.data) });
      setWorkDays(Number(monthlyDays.data?.work_days ?? 0));
    } catch (error) {
      if (!isCurrent()) return;
      setFetchError(error);
    } finally {
      if (isCurrent()) {
        setResultKey(requestKey);
        setLoading(false);
      }
    }
  }, [id, month, requestKey, beginRequest]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  if (loading || resultKey !== requestKey) return <p role="status">{t('ui.loading', { ns: 'common' })}</p>;
  if (fetchError) return <FetchFailed error={fetchError} onRetry={fetchProfileData} message={t('ui.detail-fetch-failed', { ns: 'common' })} />;

  return (
    <section className="flex flex-row w-full space-x-4">
      {/* User info or Profile */}
      <div className="bg-white rounded-md flex flex-row p-4 items-center space-x-4 w-1/4 truncate">
        <img
          src={profile?.profile_picture || user_icon}
          alt=""
          className="w-10 h-10 object-cover rounded-full"
        />
        <div className="flex flex-col text-left truncate">
          {/* EV-387（2026-10-19 実機）。電話番号の無い運転者で「+81 Unknown」と英語で出ていた。
              I12-2（2026-11-03 UAT）。携帯番号は ID で、国番号つき（+819012345678）で保存するので、そのまま出す
              （以前は「+81 」を前に付けていた）。未登録は運転者の一覧（DriverTable）と同じく「未登録」。 */}
          <h6 className="text-body-1 text-primary/400">
            {profile?.name || '-'}
          </h6>
          <p className="text-caption text-gray-600" data-testid="driver-phone">
            {profile?.phone || t('table.unregistered')}
          </p>
          <p className="text-caption text-gray-600">
            {profile?.email || '-'}
          </p>
        </div>
      </div>

      {/* Status data */}
      {/* 2026-11-03 UAT A12・A32・A35（和田様）。上部の3つは選んだ月（初めは当月）で数え、期間を明記する。
          下の行は全期間の「累計」。勤務時間の「/ 160」（根拠のない固定の値）は消し、乗務日数を足した。 */}
      <div className="flex bg-white rounded-md flex-col p-4 w-3/4">
      <p className="text-caption text-gray-600 mb-1" data-testid="status-period">
        {t('view.status.period-note', { period, range })}
      </p>
      <div className="flex flex-row space-x-4">
        {/* Monthly Submissions */}
        <div className="flex flex-1 flex-col">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {monthlySubmissions?.count}
            </p>
            <img
              src={monthly_submissions}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.monthly-submission', { period })}
            </h6>
            <p className="text-body-2 text-gray-600">
              {t('view.status.monthly-info')}
              {monthlySubmissionsTotal?.count} {t('view.status.monthly-info2')}
            </p>
          </div>
        </div>

        {/* Exceed Alcohol Limits */}
        <div className="flex flex-1 flex-col border-x px-4">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {exceedAlcoholLimit?.count}
            </p>
            <img
              src={exceed_alcohol_limit}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.exceed-alcohol', { period })}
            </h6>
            <p className="text-body-2 text-gray-600">
              {t('view.status.exceed-info')}
              {exceedAlcoholLimitTotal?.count} {t('view.status.exceed-info2')}
            </p>
          </div>
        </div>

        {/* Monthly Working Hours */}
        <div className="flex flex-1 flex-col">
          <div className="flex flex-row justify-between">
            <p className=" text-primary/300 text-title font-semi-bold">
              {/* UAT A32。分母（/ 160）を消したので、単位（時間）を添える。 */}
              {durationText(
                t,
                'ui.duration-h',
                { hours: Math.floor(Number(monthlyWorkingHours?.monthlyHours ?? 0)) },
                { ns: 'common' }
              )}
            </p>
            <img
              src={monthly_working}
              alt=""
              className="bg-primary/50 rounded-full p-2"
            />
          </div>
          <div>
            <h6 className="text-body-1 text-primary/400">
              {t('view.status.monthly-working', { period })}
            </h6>
            <p className="text-body-2 text-gray-600" data-testid="work-days">
              {t('view.status.work-days', { period, count: workDays ?? 0 })}
            </p>
            <p className="text-body-2 text-gray-600">
              {/* EV-337（2026-10-14 PM 判断: 英語は 1 だけ単数）。以前は「1 hrs total」。 */}
              {t('view.status.working-total', {
                duration: durationText(
                  t,
                  'ui.duration-h',
                  { hours: Math.floor(Number(monthlyWorkingHoursTotal?.totalHours ?? 0)) },
                  { ns: 'common' }
                ),
              })}
            </p>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default DriverViewStatus;